import React, { useState, useEffect } from 'react';
import api, { openDocument } from '../api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const UPLOAD_CATEGORIES = [
  { value: 'id', label: 'Photo ID (passport / driving licence)' },
  { value: 'right_to_work', label: 'Right to Work' },
  { value: 'p45', label: 'P45 / Starter Checklist' },
  { value: 'bank', label: 'Bank Details' },
  { value: 'other', label: 'Other' },
];

const Onboarding = () => {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [company, setCompany] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [signDoc, setSignDoc] = useState(null); // { id, title, mode }
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  const [upload, setUpload] = useState({ category: 'id', title: '', file: null });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c, p] = await Promise.all([
        api.get('/api/documents/mine'),
        api.get('/api/documents/company'),
        api.get('/api/recruitment/my-progress'),
      ]);
      setMine(m.data);
      setCompany(c.data);
      setProgress(p.data);
    } catch (err) {
      setError('Failed to load your onboarding');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const assigned = mine.filter(d => d.scope === 'assigned');
  const uploads = mine.filter(d => d.scope === 'upload');
  const toSign = assigned.filter(d => d.requires_signature && !d.signed);
  const companyToAck = company.filter(d => d.requires_signature && !d.signed);

  const openSign = (doc, mode) => { setSignDoc({ ...doc, mode }); setTypedName(''); setAgreed(false); setError(''); };

  const confirmSign = async () => {
    if (!typedName.trim()) return setError('Please type your full name');
    if (!agreed) return setError('Please tick the box to confirm');
    try {
      await api.post(`/api/documents/${signDoc.id}/sign`, { typed_name: typedName.trim() });
      setSuccess(signDoc.mode === 'acknowledge' ? 'Handbook receipt confirmed' : 'Document signed');
      setSignDoc(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!upload.file) return setError('Please choose a file');
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', upload.file);
      fd.append('category', upload.category);
      fd.append('title', upload.title || UPLOAD_CATEGORIES.find(c => c.value === upload.category)?.label || 'Document');
      await api.post('/api/documents/mine/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Document uploaded');
      setUpload({ category: 'id', title: '', file: null });
      e.target.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteUpload = async (id) => {
    if (!window.confirm('Remove this document?')) return;
    try { await api.delete(`/api/documents/mine/${id}`); load(); }
    catch (err) { setError('Failed to remove'); }
  };

  const ChecklistItem = ({ done, label, hint }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
        background: done ? 'var(--success)' : 'var(--grey-light)',
        border: done ? 'none' : '1px solid var(--border)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'
      }}>{done ? '✓' : ''}</div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--navy)' }}>{label}</div>
        {hint && <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{hint}</div>}
      </div>
    </div>
  );

  if (loading) return <div className="loader">Loading...</div>;

  const contractDone = progress && progress.contracts_to_sign > 0 && progress.contracts_signed >= progress.contracts_to_sign;
  const handbookDone = progress && progress.handbooks_to_ack > 0 && progress.handbooks_acked >= progress.handbooks_to_ack;
  const uploadsDone = progress && progress.my_uploads > 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome, {user?.name?.split(' ')[0]}</h1>
          <p className="page-subtitle">Complete the steps below to finish setting up your employment.</p>
        </div>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      {/* Progress checklist */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title">Your onboarding checklist</h3></div>
        <div className="card-body" style={{ paddingTop: '4px', paddingBottom: '8px' }}>
          <ChecklistItem done={contractDone} label="Sign your contract"
            hint={progress?.contracts_to_sign > 0 ? `${progress.contracts_signed}/${progress.contracts_to_sign} signed` : 'Waiting for your contract to be uploaded'} />
          <ChecklistItem done={handbookDone} label="Acknowledge the company handbook"
            hint={progress?.handbooks_to_ack > 0 ? `${progress.handbooks_acked}/${progress.handbooks_to_ack} acknowledged` : 'Nothing to acknowledge yet'} />
          <ChecklistItem done={uploadsDone} label="Upload your documents"
            hint={uploadsDone ? `${progress.my_uploads} uploaded` : 'ID, right to work, P45, bank details'} />
        </div>
      </div>

      {/* Action needed */}
      {(toSign.length > 0 || companyToAck.length > 0) && (
        <div className="card" style={{ marginBottom: '20px', borderTop: '3px solid var(--red)' }}>
          <div className="card-header"><h3 className="card-title">Action needed</h3></div>
          <div className="card-body" style={{ padding: 0 }}>
            {toSign.map(d => (
              <div key={d.id} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{d.title}</div><div style={hintStyle}>Contract — signature required</div></div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button>
                  <button className="btn btn-primary btn-sm" onClick={() => openSign(d, 'sign')}>Sign</button>
                </div>
              </div>
            ))}
            {companyToAck.map(d => (
              <div key={d.id} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{d.title}</div><div style={hintStyle}>Handbook / policy — please read &amp; acknowledge</div></div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>Read</button>
                  <button className="btn btn-primary btn-sm" onClick={() => openSign(d, 'acknowledge')}>Acknowledge</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your documents */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title">Your documents</h3></div>
        <div className="card-body">
          <form onSubmit={handleUpload}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label className="form-label">Type</label>
                <select className="form-control" value={upload.category} onChange={e => setUpload(u => ({ ...u, category: e.target.value }))}>
                  {UPLOAD_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label className="form-label">File</label>
                <input type="file" className="form-control" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={e => setUpload(u => ({ ...u, file: e.target.files[0] }))} />
              </div>
            </div>
            <button className="btn btn-primary btn-sm" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload document'}</button>
          </form>

          {uploads.length > 0 && (
            <div className="table-wrap" style={{ marginTop: '18px' }}>
              <table>
                <thead><tr><th>Document</th><th>Uploaded</th><th></th></tr></thead>
                <tbody>
                  {uploads.map(d => (
                    <tr key={d.id}>
                      <td>{d.title}</td>
                      <td style={hintStyle}>{format(new Date(d.created_at), 'd MMM yyyy')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button>{' '}
                        <button className="btn btn-danger btn-sm" onClick={() => deleteUpload(d.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Signed / acknowledged history */}
      {(assigned.filter(d => d.signed).length > 0 || company.filter(d => d.signed).length > 0) && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">Completed</h3></div>
          <div className="card-body" style={{ padding: 0 }}>
            {[...assigned, ...company].filter(d => d.signed).map(d => (
              <div key={`${d.scope}-${d.id}`} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{d.title}</div><div style={hintStyle}>{d.scope === 'company' ? 'Acknowledged' : 'Signed'}</div></div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className="badge badge-approved">Done</span>
                  <button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign modal */}
      {signDoc && (
        <div className="modal-overlay" onClick={() => setSignDoc(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{signDoc.mode === 'acknowledge' ? 'Acknowledge receipt' : 'Sign document'}</h3>
              <button className="modal-close" onClick={() => setSignDoc(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                <strong style={{ color: 'var(--navy)' }}>{signDoc.title}</strong>
              </p>
              <div className="form-group">
                <label className="form-label">Type your full name to {signDoc.mode === 'acknowledge' ? 'confirm' : 'sign'}</label>
                <input className="form-control" value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="e.g. Jane Smith" />
              </div>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: 'var(--navy)' }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ accentColor: 'var(--red)', marginTop: '2px' }} />
                <span>{signDoc.mode === 'acknowledge'
                  ? 'I confirm I have received and read this document.'
                  : 'I confirm I have read and agree to this document, and that typing my name counts as my signature.'}</span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSignDoc(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmSign}>{signDoc.mode === 'acknowledge' ? 'Confirm' : 'Sign'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const rowStyle = { padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' };
const hintStyle = { fontSize: '12px', color: 'var(--grey-mid)', marginTop: '2px' };

export default Onboarding;
